import React, { useEffect, useRef, useState } from 'react'
import { useI18n } from '@ggs/i18n'
import { useModal } from '@ggs/ui/common'
import { Box, Link } from '@mui/material'
import { DataTable } from '../'
import { Loader } from '../../loader'
import { get, size, isEmpty, sortBy, uniqBy } from 'lodash'
import { WeeklyTimeTable } from '../../calendar'
import { useDispatch, addNotification, useSelector } from '@ggs/store'
import { useAcademicApi, useAcademicExpApi, useAcademicDataDigest } from '../../../local'
import './ProgramStudyTimetablesTable.scss'

/**
 * @typedef {import('../../../definitions').ProgramStudyTimetableRecord} ProgramStudyTimetableRecord
 * @typedef {import('mui-datatables').MUIDataTableOptions} MUIDataTableOptions
 */

/**
 * @typedef {Object} TimetableModal
 * @property {string} title
 * @property {JSX.Element} content
 */

/**
 * Program studies STT table.
 * @return {JSX.Element}
 */
const ProgramStudyTimetablesTable = () => {
  const { t } = useI18n()
  // const { } = useAcademicApi()
  const isStorybook = `${process.env.STORYBOOK}` === 'yes'
  const dispatch = useDispatch()

  /**
   * @type {ProgramStudyTimetableRecord[]}
   */
  const defaultDataTable = null
  const [dataTable, setDataTable] = useState(defaultDataTable)
  const [loading, setLoading] = useState(true)
  const [currentPageIndex, setCurrentPageIndex] = useState(0)
  const [sttLink, setSttLink] = useState(true)

  const standardTimetableId = get(
    window,
    'drupalSettings.react.standardTimetableId',
    'ce4e83d4-0ae3-4530-5525-c58e33452b8f' // UBC-V, 2022-2023 Winter Term 1
  )

  const { getCourseSections } = useAcademicExpApi()
  const { getStandardTimetables, getStandardTimetable } = useAcademicApi()
  const { digestCourseSectionsTimetable } = useAcademicDataDigest()
  const academicPeriod = useSelector((state) => state.settings.academicPeriod)

  // Page size for subjects retrieved with API request.
  const pageSize = 500
  // Rows presented for each table page.
  const rowsPerPage = 20

  /**
   * @type {TimetableModal}
   */
  const defaultModalData = {
    title: '',
    content: null,
  }
  const modalData = useRef(defaultModalData)

  const { modal, triggerModal } = useModal({
    scroll: 'body',
    title: () => (
      <Box className="modal-title">
        <h2>{modalData.current.title}</h2>
      </Box>
    ),
    content: () => {
      return <Box>{modalData.current.content}</Box>
    },
  })

  const enableLoading = () => {
    setLoading(true)
  }

  const disableLoading = () => {
    setLoading(false)
  }

  /**
   * Receive current page index.
   * @param {number} currentPage
   */
  const onPageChange = (currentPage) => {
    setCurrentPageIndex(currentPage)
  }

  /**
   * Handle click on timetable title.
   * @param {React.MouseEvent<HTMLAnchorElement>} e
   */
  const handleLinkClick = (e) => {
    // Prevent opening target link when running in SB.
    if (isStorybook) {
      e.preventDefault()
      alert('Timetable detail route will open when running in Drupal.')
    }
  }

  /**
   * Open timetable modal.
   * @param {TimetableModal} tableColumn
   */
  const openModal = (tableColumn) => {
    modalData.current = {
      title: tableColumn.title,
      content: tableColumn.content,
    }

    triggerModal()
  }

  /**
   * Resolve program of studies course section data.
   */
  const resolveProgramStudySectionsData = async () => {
    enableLoading()
    const standardTimetable = await getStandardTimetable(standardTimetableId)
    if (!standardTimetable) {
      dispatch(
        addNotification(
          'not_found_stt_error',
          'error',
          t('Standard timetable not found for {{id}}', { id: standardTimetableId })
        )
      )

      return
    }

    const standardTimetables = await getStandardTimetables({
      pageSize,
      academicPeriodId: [academicPeriod],
      status: 'Published',
      programArea: standardTimetable.programArea,
    })
    await Promise.all(
      standardTimetables.pageItems.map(async (timetable) => {
        const courseSectionIds = standardTimetable.courseSectionIds || []
        console.log(courseSectionIds.length)
        if (courseSectionIds.length === 0) {
          setSttLink(false)
        }

        const courseSectionsResult = await getCourseSections({
          pageSize,
          courseSectionId: courseSectionIds,
        })

        const courseSections = courseSectionsResult?.pageItems || null
        const timeTableData = digestCourseSectionsTimetable(courseSections, timetable)

        // console.log(timeTableData)
        setDataTable((table) =>
          Array.isArray(table)
            ? sortBy(uniqBy([...table, ...timeTableData], 'timetableId'), ['year', 'name'])
            : timeTableData
        )
      })
    )


    // const courseSectionIds = standardTimetable.courseSectionIds || []
    // console.log(courseSectionIds.length)
    // if (courseSectionIds.length === 0) {
    //   setSttLink(false)
    // }

    // const courseSectionsResult = await getCourseSections({
    //   pageSize,
    //   courseSectionId: courseSectionIds,
    // })

    // const courseSections = courseSectionsResult?.pageItems || null
    // const timeTableData = digestCourseSectionsTimetable(courseSections, standardTimetable)

    // console.log(timeTableData)
    // setDataTable((table) =>
    //   Array.isArray(table)
    //     ? sortBy(uniqBy([...table, ...timeTableData], 'timetableId'), ['year', 'name'])
    //     : timeTableData
    // )

    disableLoading()
  }

  /**
   * @type {MUIDataTableOptions}
   */
  const options = {
    page: currentPageIndex,
    onChangePage: onPageChange,
    jumpToPage: false,
    pagination: true,
    rowsPerPage,
    textLabels: {
      body: {
        noMatch: loading ? t('Please wait...') : t('No results found'),
      },
    },
  }

  useEffect(() => {
    resolveProgramStudySectionsData().then(() =>
      console.log('Resolving program of studies timetables data')
    )
  }, [])

  if (loading) {
    return <Loader />
  }

  /**
   * Custom render function for timetable title column.
   * @param {number} dataIndex
   * @return {JSX.Element}
   */
  const renderTitleLink = (dataIndex) => {
    const name = dataTable[dataIndex].name
    const timetableId = dataTable[dataIndex].timetableId
    if (sttLink) {
      return (
        <Link
          onClick={handleLinkClick}
          href={`/browse-timetables/timetable/${timetableId}`}
        >
          {name}
        </Link>
      )
    } else {
      return <div>{name}</div>
    }
  }

  /**
   * Custom render function for timetable description column.
   * @param {number} dataIndex
   * @return {JSX.Element}
   */
  const renderDescription = (dataIndex) => {
    const description = dataTable[dataIndex].description
    return <div dangerouslySetInnerHTML={{ __html: description }} />
  }

  /**
   * Custom render function for timetable calendar modal link.
   * @param {number} dataIndex
   * @return {JSX.Element}
   */
  const renderTimetableLink = (dataIndex) => {
    const courseSection = dataTable[dataIndex]
    const meetings = dataTable[dataIndex].meetings
    const timetableModal = {
      title: dataTable[dataIndex].name,
      content: <WeeklyTimeTable timeTableList={meetings} />,
    }

    return (
      <>
        {!isEmpty(meetings) && sttLink ? (
          <Link
            onClick={(e) => {
              e.preventDefault()
              openModal(timetableModal)
            }}
            href="#"
          >
            {t('View Timetable')}
          </Link>
        ) : (
          t('No timetable available')
        )}
      </>
    )
  }

  const tableColumns = [
    {
      name: 'name',
      label: t('STT Name'),
      options: {
        filter: false,
        sort: false,
        customBodyRenderLite: renderTitleLink,
      },
    },
    {
      name: 'description',
      label: t('STT Description'),
      options: {
        filter: false,
        sort: false,
        customBodyRenderLite: renderDescription,
      },
    },
    // {
    //   name: 'timetable',
    //   label: t('Timetable'),
    //   options: {
    //     filter: false,
    //     sort: false,
    //     customBodyRenderLite: renderTimetableLink,
    //   },
    // },
    {
      name: 'year',
      label: t('Year'),
      options: {
        filter: false,
        sort: true,
      },
    },
  ]

  const rowsCount = size(dataTable)

  return (
    <Box className="program-study-timetables">
      <div className="program-study-timetables__table">
        <DataTable
          className="data-table"
          title={''}
          data={dataTable}
          columns={tableColumns}
          options={{
            ...options,
            count: rowsCount,
          }}
        />
      </div>
      {modal}
    </Box>
  )
}

export default ProgramStudyTimetablesTable
